import { Routes, Route, Navigate, useLocation } from "react-router";
import { lazy, Suspense, useEffect } from "react";
import { LandingPage } from "@/components/home/landing-page";
import { NotFound } from "@/components/not-found";
import { Toaster } from "@/components/ui/sonner";

const LoginPage = lazy(() =>
  import("@/pages/Login").then((m) => ({ default: m.LoginPage })),
);
const VerifyEmailPage = lazy(() =>
  import("@/pages/VerifyEmail").then((m) => ({ default: m.VerifyEmailPage })),
);
const ProtectedRoute = lazy(() =>
  import("@/pages/ProtectedRoute").then((m) => ({ default: m.ProtectedRoute })),
);
const DashboardLayout = lazy(() =>
  import("@/components/dashboard/DashboardLayout").then((m) => ({
    default: m.DashboardLayout,
  })),
);
const Startseite = lazy(() =>
  import("@/components/dashboard/Startseite").then((m) => ({
    default: m.Startseite,
  })),
);
const PlaceholderPage = lazy(() =>
  import("@/components/dashboard/PlaceholderPage").then((m) => ({
    default: m.PlaceholderPage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/pages/Dashboard").then((m) => ({ default: m.DashboardPage })),
);
const DocumentViewPage = lazy(() =>
  import("@/pages/DocumentView").then((m) => ({ default: m.DocumentViewPage })),
);
const ImpressumPage = lazy(() =>
  import("@/pages/Impressum").then((m) => ({ default: m.ImpressumPage })),
);
const DatenschutzPage = lazy(() =>
  import("@/pages/Datenschutz").then((m) => ({ default: m.DatenschutzPage })),
);
const DemoPage = lazy(() =>
  import("@/pages/Demo").then((m) => ({ default: m.DemoPage })),
);

export function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route index element={<LandingPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route
            path="api/auth/verify-email"
            element={<VerifyEmailPage />}
          />

          {/* Dashboard layout wraps all authenticated routes */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Startseite />} />
            <Route path="qualitaetspruefung" element={<DashboardPage />} />
            <Route
              path="qualitaetspruefung/:id"
              element={<DocumentViewPage />}
            />
            <Route
              path="vorlagen"
              element={<PlaceholderPage title="Vorlagen" />}
            />
            <Route
              path="wissensdatenbank"
              element={<PlaceholderPage title="Wissensdatenbank" />}
            />
            <Route
              path="berichte"
              element={<PlaceholderPage title="Berichte" />}
            />
            <Route
              path="einstellungen"
              element={<PlaceholderPage title="Einstellungen" />}
            />
          </Route>

          {/* Redirects from old routes */}
          <Route
            path="documents/:id"
            element={<RedirectToQualitaetspruefung />}
          />

          {/* Demo / contact page */}
          <Route path="demo" element={<DemoPage />} />

          {/* Legal pages (outside dashboard layout) */}
          <Route path="impressum" element={<ImpressumPage />} />
          <Route path="datenschutz" element={<DatenschutzPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster />
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RedirectToQualitaetspruefung() {
  // Use a wrapper because Navigate doesn't support dynamic params inline in path
  const path = window.location.pathname.replace(
    /^\/documents\//,
    "/dashboard/qualitaetspruefung/",
  );
  return <Navigate to={path} replace />;
}
